import { useState, useEffect } from 'react';
import api from '../services/api';
import { useI18n } from '../i18n';

const ABSENCE_BADGE = {
  KRANK_MIT_ATTEST: 'bg-danger',
  KRANK_OHNE_ATTEST: 'bg-warning text-dark',
  FZT: 'badge-fzt',
};

export default function YearView() {
  const { t } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/time/summary/year/${year}`),
      api.get(`/absences/?year=${year}`),
    ])
      .then(([r, absR]) => {
        setData(r.data);
        setAbsences(absR.data);
      })
      .finally(() => setLoading(false));
  }, [year]);

  const formatMins = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-3 mb-4">
        <h4 className="mb-0"><i className="bi bi-calendar-range me-2" />{t('year.title')}</h4>
        <select className="form-select form-select-sm w-auto" value={year} onChange={(e) => setYear(+e.target.value)}>
          {[now.getFullYear()-2, now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => <option key={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border" /></div>
      ) : data ? (
        <>
          <div className="card mb-4">
            <div className="card-body text-center">
              <span className="fs-3 fw-bold text-primary">{formatMins(data.total_worked_minutes)}</span>
              <span className="text-muted ms-2">{t('year.totalWorked', { year })}</span>
            </div>
          </div>

          <div className="row g-3">
            {data.months.map((m) => (
              <div className="col-md-4 col-sm-6" key={m.month}>
                <div className="card h-100">
                  <div className="card-header d-flex justify-content-between">
                    <strong>{t(`year.m${m.month}`)}</strong>
                    <span className="badge bg-primary">{t('year.days', { n: m.total_days })}</span>
                  </div>
                  <div className="card-body py-2">
                    {m.total_days === 0 ? (
                      <span className="text-muted small">{t('year.noEntries')}</span>
                    ) : (
                      <>
                        <div className="fw-bold">{formatMins(m.total_worked_minutes)}</div>
                        <div className="progress mt-1" style={{ height: 6 }}>
                          <div
                            className="progress-bar bg-success"
                            style={{ width: `${Math.min(100, Math.round((m.total_worked_minutes / (m.total_days * 480)) * 100))}%` }}
                          />
                        </div>
                        <div className="text-muted small mt-1">
                          {t('year.avgPerDay', { time: formatMins(Math.round(m.total_worked_minutes / m.total_days)) })}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {absences.length > 0 && (
            <div className="card mt-4">
              <div className="card-header">
                <i className="bi bi-bandaid me-2" />
                <strong>{t('year.absencesTitle', { year })}</strong>
                <span className="badge bg-secondary ms-2">{absences.length}</span>
              </div>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>{t('time.absence.type')}</th>
                      <th>{t('time.absence.from')}</th>
                      <th>{t('time.absence.to')}</th>
                      <th>{t('time.absence.workDays')}</th>
                      <th>{t('time.absence.notes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <span className={`badge ${ABSENCE_BADGE[a.absence_type]}`}>
                            {t(`time.absence.${a.absence_type}`)}
                          </span>
                        </td>
                        <td>{a.start_date}</td>
                        <td>{a.end_date}</td>
                        <td>{a.days_count}</td>
                        <td>{a.notes || '–'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
